/**
 * HelpProvider — open/tab/article/search state for the help panel, plus the shared chat conversation.
 *
 * Mounted once above the routes so the panel keeps its place while the landlord moves between screens.
 * It derives the current pathname from the router location and makes no request until the panel (or a
 * /help page) is open (NFR-PERF-4). A change of signed-in landlord drops everything, including cached
 * help queries, so one landlord's conversation is never shown to the next (BR-29).
 */

import { createContext, memo, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { helpKeys, isHelpChatDisabled, useHelpConversations } from '../../lib/api.help';
import { navGroups } from '../navigation/nav-config';
import { useHelpChat, type HelpChat } from './useHelpChat';

export type HelpTab = 'articles' | 'ask';

export interface HelpTopic {
  category: string;
  label: string;
}

export interface HelpContextValue {
  open: boolean;
  tab: HelpTab;
  /** The article shown in the panel; null shows the tab content. */
  articleId: string | null;
  /** The topic (a category's article list) shown in the panel; articles opened from it come back to it. */
  topic: HelpTopic | null;
  searchQuery: string;
  /** The conversation list replaces the Ask tab's thread. */
  historyOpen: boolean;
  /** The router pathname (without the /landlord basename) the landlord is on. */
  pathname: string;
  /** The name of the screen the landlord is on (e.g. "Leases"), when it is a navigation destination. */
  pageName: string | null;
  /** True when the chat kill switch is off: the chat entry is hidden and only the help center remains. */
  chatDisabled: boolean;
  chat: HelpChat;
  openPanel: (target?: { tab?: HelpTab; articleId?: string }) => void;
  closePanel: () => void;
  togglePanel: () => void;
  setTab: (tab: HelpTab) => void;
  openArticle: (id: string) => void;
  closeArticle: () => void;
  openTopic: (category: string, label: string) => void;
  closeTopic: () => void;
  setSearchQuery: (query: string) => void;
  setHistoryOpen: (open: boolean) => void;
}

const NOOP = () => {};

/** What components outside a provider (isolated tests) see: a closed panel that ignores every call. */
const INERT_CHAT: HelpChat = {
  conversationId: null,
  messages: [],
  streamingText: '',
  phase: 'idle',
  isLoadingConversation: false,
  loadError: null,
  draft: '',
  inputError: null,
  rateLimit: null,
  disabled: false,
  setDraft: NOOP,
  send: async () => {},
  retry: async () => {},
  newConversation: NOOP,
  loadConversation: async () => {},
  applyRating: NOOP,
  escalate: async () => null,
};

const INERT: HelpContextValue = {
  open: false,
  tab: 'articles',
  articleId: null,
  topic: null,
  searchQuery: '',
  historyOpen: false,
  pathname: '/',
  pageName: null,
  chatDisabled: false,
  chat: INERT_CHAT,
  openPanel: NOOP,
  closePanel: NOOP,
  togglePanel: NOOP,
  setTab: NOOP,
  openArticle: NOOP,
  closeArticle: NOOP,
  openTopic: NOOP,
  closeTopic: NOOP,
  setSearchQuery: NOOP,
  setHistoryOpen: NOOP,
};

const HelpContext = createContext<HelpContextValue>(INERT);

// eslint-disable-next-line react-refresh/only-export-components -- hook colocated with its provider (house pattern)
export function useHelp(): HelpContextValue {
  return useContext(HelpContext);
}

/** The navigation label of the screen at `pathname` (longest matching nav path), e.g. /leases/abc -> "Leases". */
function pageNameFor(pathname: string): string | null {
  let best: { label: string; length: number } | null = null;
  for (const group of navGroups) {
    for (const item of group.items) {
      const matches = pathname === item.path || pathname.startsWith(`${item.path}/`);
      if (matches && (!best || item.path.length > best.length)) best = { label: item.label, length: item.path.length };
    }
  }
  return best?.label ?? null;
}

/**
 * Holds the help state and publishes it through `onChange`. It renders no children: it is keyed by
 * landlord to drop its state on a landlord change (BR-29), and a keyed wrapper around the app would
 * remount every route (replaying a single-use magic-link verify) when the landlord signs in. Memoized
 * so only its own state changes re-publish, not its parent's re-render.
 */
const HelpState = memo(function HelpState({ signedIn, onChange }: { signedIn: boolean; onChange: (value: HelpContextValue) => void }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [tab, setTabState] = useState<HelpTab>('articles');
  const [articleId, setArticleId] = useState<string | null>(null);
  const [topic, setTopic] = useState<HelpTopic | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const chat = useHelpChat(pathname);

  // The help center pages are an "open" too: their reads are allowed, an idle screen's are not.
  const helpSurfaceActive = signedIn && (open || pathname === '/help' || pathname.startsWith('/help/'));
  // The conversation list is the cheapest chat route, so it doubles as the kill-switch probe (503 HELP_CHAT_DISABLED).
  const conversations = useHelpConversations(helpSurfaceActive);
  const chatDisabled = chat.disabled || isHelpChatDisabled(conversations.error);

  const openPanel = useCallback((target?: { tab?: HelpTab; articleId?: string }) => {
    if (target?.tab) setTabState(target.tab);
    if (target?.articleId !== undefined) {
      setArticleId(target.articleId);
      setTopic(null);
    }
    setOpen(true);
  }, []);
  const closePanel = useCallback(() => setOpen(false), []);
  const togglePanel = useCallback(() => setOpen((current) => !current), []);
  const setTab = useCallback((next: HelpTab) => {
    setTabState(next);
    setArticleId(null);
    setTopic(null);
    setHistoryOpen(false);
  }, []);
  const openArticle = useCallback((id: string) => setArticleId(id), []);
  const closeArticle = useCallback(() => setArticleId(null), []);
  const openTopic = useCallback((category: string, label: string) => setTopic({ category, label }), []);
  const closeTopic = useCallback(() => setTopic(null), []);

  // When the chat is switched off, an open Ask tab falls back to the articles view.
  useEffect(() => {
    if (chatDisabled) {
      setTabState('articles');
      setHistoryOpen(false);
    }
  }, [chatDisabled]);

  const value = useMemo<HelpContextValue>(
    () => ({
      open,
      tab,
      articleId,
      topic,
      searchQuery,
      historyOpen,
      pathname,
      pageName: pageNameFor(pathname),
      chatDisabled,
      chat,
      openPanel,
      closePanel,
      togglePanel,
      setTab,
      openArticle,
      closeArticle,
      openTopic,
      closeTopic,
      setSearchQuery,
      setHistoryOpen,
    }),
    [
      open,
      tab,
      articleId,
      topic,
      searchQuery,
      historyOpen,
      pathname,
      chatDisabled,
      chat,
      openPanel,
      closePanel,
      togglePanel,
      setTab,
      openArticle,
      closeArticle,
      openTopic,
      closeTopic,
    ]
  );

  useLayoutEffect(() => onChange(value), [value, onChange]);
  useLayoutEffect(() => () => onChange(INERT), [onChange]);

  return null;
});

export function HelpProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { manager } = useAuth();
  const landlordId = manager?.id ?? null;

  // A different landlord (or a sign-out) starts from nothing: no cached help data, no open panel, no thread.
  useEffect(() => {
    return () => {
      void queryClient.removeQueries({ queryKey: helpKeys.all });
    };
  }, [queryClient, landlordId]);

  const [value, setValue] = useState<HelpContextValue>(INERT);

  return (
    <HelpContext.Provider value={value}>
      <HelpState key={landlordId ?? 'signed-out'} signedIn={landlordId !== null} onChange={setValue} />
      {children}
    </HelpContext.Provider>
  );
}
